import { Box, Burger, Drawer, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import type * as React from "react";
import { Link } from "react-router";
import { PANEL_BORDER, PANEL_COLOR, PANEL_FONT, PANEL_LAYOUT, PANEL_SPACE } from "../tokens";
import { Wordmark } from "./Wordmark";

/**
 * The signed-in frame (Figma Home and every admin frame): a white header (wordmark left,
 * `headerEnd` right), a 247px white sidebar under a "Menu" heading, the page on the
 * `surface/background` gray (a column at most 1040px wide, centered on wider screens), and
 * a white footer. Below the `sm` breakpoint the sidebar
 * moves into a drawer behind a menu button.
 */
export function PanelShell({
  headerEnd,
  nav,
  navOpen,
  onNavToggle,
  onNavClose,
  children,
}: {
  /** The account menu, plus anything that flags the environment. */
  headerEnd: React.ReactNode;
  nav: React.ReactNode;
  navOpen: boolean;
  onNavToggle: () => void;
  onNavClose: () => void;
  children: React.ReactNode;
}) {
  const menu = (
    <Stack gap={5}>
      <Text fw={600} lh={1.25} py={PANEL_SPACE.field}>
        Menu
      </Text>
      {nav}
    </Stack>
  );
  return (
    <Box mih="100vh" bg={PANEL_COLOR.surface} style={{ display: "flex", flexDirection: "column" }}>
      <Box component="header" bg="white" p={PANEL_SPACE.page} style={{ borderBottom: PANEL_BORDER }}>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={navOpen} onClick={onNavToggle} hiddenFrom="sm" size="sm" aria-label="Menu" />
            <UnstyledButton component={Link} to="/" aria-label="SlideBrain home">
              <Wordmark />
            </UnstyledButton>
          </Group>
          {headerEnd}
        </Group>
      </Box>
      <Box style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <Box
          component="nav"
          aria-label="Panel"
          visibleFrom="sm"
          bg="white"
          w={PANEL_LAYOUT.navWidth}
          p={16}
          style={{ flexShrink: 0, borderRight: PANEL_BORDER }}
        >
          {menu}
        </Box>
        <Box component="main" p={PANEL_SPACE.page} style={{ flex: 1, minWidth: 0 }}>
          <Box maw={PANEL_LAYOUT.contentMaxWidth} mx="auto">
            {children}
          </Box>
        </Box>
      </Box>
      <Box component="footer" bg="white" p={PANEL_SPACE.page} style={{ borderTop: PANEL_BORDER }}>
        <Text fz={PANEL_FONT.small} ta="center" lh={1.25}>
          {new Date().getFullYear()} © Slide Science
        </Text>
      </Box>
      <Drawer opened={navOpen} onClose={onNavClose} size={PANEL_LAYOUT.navWidth} hiddenFrom="sm" title="SlideBrain">
        <Box component="nav" aria-label="Panel">
          {menu}
        </Box>
      </Drawer>
    </Box>
  );
}
